import { createWlinkI18n } from "@wlink/commons/i18n";
const i18n = createWlinkI18n();
const { t } = i18n.global;
/**
 * 通用确认弹窗 composable
 * 用于处理未保存修改时的确认逻辑
 *
 * ## 功能说明
 *
 * 提供统一的确认弹窗组件，主要用于处理以下场景：
 * - 页面/Tab切换时检查未保存修改
 * - 路由离开时检查未保存修改
 * - 表单取消/重置时确认是否保存
 * - 其他需要用户确认保存操作的场景
 *
 * ## 基本用法
 *
 * ### 1. 基础确认弹窗
 * ```typescript
 * import { useConfirmDialog } from '@wlink/commons/core'
 *
 * const { showSaveConfirmDialog } = useConfirmDialog()
 *
 * const handleCancel = async () => {
 *   const result = await showSaveConfirmDialog()
 *
 *   if (result === 'save') {
 *     // 用户选择保存
 *     const success = await saveData()
 *     if (success) {
 *       // 保存成功，执行后续操作
 *       goBack()
 *     }
 *   } else if (result === 'discard') {
 *     // 用户选择不保存，直接执行后续操作
 *     goBack()
 *   }
 *   // result === 'cancel' 时不执行任何操作
 * }
 * ```
 *
 * ### 2. 自定义弹窗选项
 * ```typescript
 * const result = await showSaveConfirmDialog({
 *   title: '确认离开',
 *   message: '当前表单有未保存的修改，是否保存？',
 *   confirmButtonText: '保存并离开',
 *   cancelButtonText: '直接离开',
 *   type: 'warning'
 * })
 * ```
 *
 * ### 3. 使用未保存修改检查 composable
 * ```typescript
 * import { useUnsavedChanges } from '@wlink/commons/core'
 *
 * const checkUnsavedChanges = () => {
 *   return formData.value.name !== originalData.value.name
 * }
 *
 * const saveChanges = async () => {
 *   const response = await api.saveForm(formData.value)
 *   return response.success
 * }
 *
 * const { handleUnsavedChanges } = useUnsavedChanges(
 *   checkUnsavedChanges,
 *   saveChanges,
 *   { title: '保存表单数据' }
 * )
 *
 * // 在需要检查的地方调用
 * const result = await handleUnsavedChanges()
 * if (result !== 'cancel') {
 *   // 继续执行操作
 *   router.push('/next-page')
 * }
 * ```
 *
 * ### 4. 路由守卫中使用
 * ```typescript
 * import { onBeforeRouteLeave } from 'vue-router'
 * import { useConfirmDialog } from '@wlink/commons/core'
 *
 * const { showSaveConfirmDialog } = useConfirmDialog()
 *
 * onBeforeRouteLeave(async (to, from, next) => {
 *   if (hasUnsavedChanges()) {
 *     const result = await showSaveConfirmDialog()
 *
 *     if (result === 'save') {
 *       const success = await saveData()
 *       next(success)
 *     } else if (result === 'discard') {
 *       next()
 *     } else {
 *       next(false) // 取消导航
 *     }
 *   } else {
 *     next()
 *   }
 * })
 * ```
 *
 * ## API 参考
 *
 * ### ConfirmDialogOptions
 * | 属性 | 类型 | 默认值 | 说明 |
 * |-----|------|--------|------|
 * | title | string | "提示" | 弹窗标题 |
 * | message | string | "是否保存当前页面信息？" | 弹窗内容 |
 * | confirmButtonText | string | "保存" | 确认按钮文字 |
 * | cancelButtonText | string | "不保存" | 取消按钮文字 |
 * | type | "info" \| "success" \| "warning" \| "error" | "warning" | 弹窗类型 |
 *
 * ### ConfirmDialogResult
 * | 值 | 说明 |
 * |----|------|
 * | "save" | 用户选择保存 |
 * | "discard" | 用户选择不保存 |
 * | "cancel" | 用户取消操作 |
 *
 * ### useConfirmDialog()
 * 返回：
 * - `showSaveConfirmDialog(options?: ConfirmDialogOptions): Promise<ConfirmDialogResult>`
 *
 * ### useUnsavedChanges(checkFn, saveFn, options?)
 * 参数：
 * - `checkFn: () => boolean` - 检查是否有未保存修改的函数
 * - `saveFn: () => Promise<boolean>` - 保存修改的函数，返回是否保存成功
 * - `options?: ConfirmDialogOptions` - 可选的弹窗配置
 *
 * 返回：
 * - `handleUnsavedChanges(): Promise<ConfirmDialogResult>` - 处理未保存修改的函数
 * - `showSaveConfirmDialog(options?: ConfirmDialogOptions): Promise<ConfirmDialogResult>` - 显示确认弹窗的函数
 *
 * ## 注意事项
 *
 * 1. 弹窗基于 Element Plus 的 MessageBox 组件实现
 * 2. 确认弹窗会区分"取消"和"关闭"操作，都会返回"cancel"
 * 3. 建议在保存操作中添加适当的错误处理
 * 4. 在路由守卫中使用时，注意处理异步操作
 * 5. 如果需要自定义弹窗样式，可以通过 Element Plus 的全局配置修改
 *
 * @author Generated by AI Assistant
 * @since 1.0.0
 */

export interface ConfirmDialogOptions {
  title?: string;
  message?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  type?: "info" | "success" | "warning" | "error";
}

export type ConfirmDialogResult = "save" | "discard" | "cancel";

/**
 * 显示保存确认弹窗
 * @param options 弹窗配置选项
 * @returns Promise<ConfirmDialogResult>
 */
export const useConfirmDialog = () => {
  const showSaveConfirmDialog = async (
    options?: ConfirmDialogOptions
  ): Promise<ConfirmDialogResult> => {
    const defaultOptions: ConfirmDialogOptions = {
      title: t("60985"),
      message: t("67327"),
      confirmButtonText: t("60971"),
      cancelButtonText: t("67328"),
      type: "warning",
    };

    const finalOptions = { ...defaultOptions, ...options };

    try {
      const { ElMessageBox } = await import("element-plus");
      await ElMessageBox.confirm(finalOptions.message!, finalOptions.title!, {
        confirmButtonText: finalOptions.confirmButtonText!,
        cancelButtonText: finalOptions.cancelButtonText!,
        type: finalOptions.type!,
        distinguishCancelAndClose: true,
      });
      return "save";
    } catch (action) {
      if (action === "cancel") {
        return "discard";
      }
      return "cancel";
    }
  };

  return {
    showSaveConfirmDialog,
  };
};

/**
 * 处理未保存修改的 composable
 * @param checkUnsavedChanges 检查是否有未保存修改的函数
 * @param saveChanges 保存修改的函数
 * @param options 确认弹窗配置选项
 */
export const useUnsavedChanges = (
  checkUnsavedChanges: () => boolean,
  saveChanges: () => Promise<boolean>,
  options?: ConfirmDialogOptions
) => {
  const { showSaveConfirmDialog } = useConfirmDialog();

  /**
   * 处理未保存修改的确认逻辑
   * @returns Promise<ConfirmDialogResult>
   */
  const handleUnsavedChanges = async (): Promise<ConfirmDialogResult> => {
    if (checkUnsavedChanges()) {
      const result = await showSaveConfirmDialog(options);

      if (result === "save") {
        const saveSuccess = await saveChanges();
        return saveSuccess ? "save" : "cancel";
      }

      return result;
    }

    return "save"; // 没有未保存的修改，直接允许继续
  };

  return {
    handleUnsavedChanges,
    showSaveConfirmDialog,
  };
};
